import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const priorityColors = {
  low: "#16A34A",
  medium: "#D97706",
  high: "#EA580C",
  urgent: "#DC2626",
};

export default function PortalNewRequest() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10 MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setAttachments((prev) => [
          ...prev,
          { filename: file.name, content_type: file.type, data_b64: b64 },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/portal/requests", {
        title: form.title,
        description: form.description,
        priority: form.priority,
        attachments,
        client_id: "",
      });
      toast.success("Task submitted successfully!");
      navigate(`/portal/requests/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout>
      <div data-testid="portal-new-request-page" className="max-w-2xl">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate("/portal/requests")} className="p-1.5 rounded-lg hover:bg-white" style={{ color: "#A8A29E" }}>
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>New Task</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>Tell us what you need</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-8" style={{ borderColor: "#E7E5E4" }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                Task Title *
              </Label>
              <Input
                data-testid="request-title-input"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Update homepage hero section"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "#57534E" }}>
                Description *
              </Label>
              <RichTextEditor
                value={form.description}
                onChange={(val) => setForm({ ...form, description: val })}
                placeholder="Please describe the work you need in as much detail as possible..."
                minHeight={128}
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "#57534E" }}>
                Priority
              </Label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`priority-${opt.value}`}
                    onClick={() => setForm({ ...form, priority: opt.value })}
                    className="flex-1 py-2 px-3 rounded-lg border text-xs font-semibold transition-all"
                    style={{
                      borderColor: form.priority === opt.value ? priorityColors[opt.value] : "#E7E5E4",
                      background: form.priority === opt.value ? priorityColors[opt.value] + "15" : "#fff",
                      color: form.priority === opt.value ? priorityColors[opt.value] : "#57534E",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "#57534E" }}>
                Attachments
              </Label>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
                data-testid="file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors hover:bg-stone-50"
                style={{ borderColor: "#E7E5E4", color: "#57534E" }}
                data-testid="attach-file-button"
              >
                <Paperclip size={14} />
                Attach files
              </button>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: "#F5F3EF" }}>
                      <span className="text-xs truncate mr-2" style={{ color: "#292524" }}>{att.filename}</span>
                      <button type="button" onClick={() => removeAttachment(i)} style={{ color: "#A8A29E" }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate("/portal/requests")}>Cancel</Button>
              <Button
                data-testid="submit-request-button"
                type="submit"
                disabled={saving}
                style={{ background: "#D97706", color: "#fff" }}
              >
                {saving ? "Submitting..." : "Submit Task"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </PortalLayout>
  );
}
